/* Čekající pozvánka do týmu — připomínka pro zvoneček (NotificationCenter).
 *
 * PROČ TOHLE EXISTUJE (9. 9. 2026, zadání majitele)
 * ------------------------------------------------
 * Manažer pozve poradce přes /api/manager/invite-advisor. Backend pozvánku
 * uloží, poradce ji může přijmout — a NIKDE SE O NÍ NEDOZVÍ. Karta
 * `MujManazerKarta` je zapojená jediným místem: dole v Nastavení. Poradce, který
 * do Nastavení nezajde, nechá pozvánku vypršet, aniž by tušil, že přišla.
 * Manažer pak vidí „čeká na přijetí" a myslí si, že poradce váhá.
 *
 * PROČ TO PATŘÍ DO ZVONEČKU
 * -------------------------
 * Zvoneček má pravidlo „budoucnost sem nepatří" a dvě přiznané výjimky:
 * rezervace klienta a týmovou akci. Obě mají týž důvod — poradce se o nich
 * jinak nedozví vůbec. Pozvánka je třetí případ přesně téhož druhu, ne nový
 * proud. Proto sem, a ne další notifikační systém.
 *
 * ROZHODNUTÍ: POZVÁNKA NEZMIZÍ PŘEČTENÍM
 * --------------------------------------
 * Ostatní připomínky odznak zhasnou tím, že si poradce panel otevře — jsou to
 * ZPRÁVY. Pozvánka je ÚKOL: dokud ji nepřijme nebo neodmítne, je pořád co
 * dělat, a mlčky vyprší. Proto ji `pozvankaSviti()` drží svítící až do
 * vyřízení. Je to vědomá výjimka, ne opomenutí.
 *
 * CO TO NEDĚLÁ
 * ------------
 * Nic se neodesílá — žádný e-mail ani push. Uvidí ji ten, kdo si appku otevře.
 * Stejné omezení jako u týmových akcí (viz akceNotif.js).
 */

/** Kolik dní před vypršením hlásit pozvánku jako naléhavou. */
export const NALEHAVE_DNI = 3;

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/**
 * Počet celých dní od `ted` do ISO termínu.
 * `null` = termín nejde přečíst — volající pak lhůtu NEDOMÝŠLÍ.
 */
export function dnuDoVyprseni(iso, ted = new Date()) {
  if (typeof iso !== "string" || !iso) return null;
  const cil = new Date(iso);
  if (Number.isNaN(cil.getTime())) return null;
  const zaklad = new Date(ted);
  if (Number.isNaN(zaklad.getTime())) return null;
  return Math.round((startOfDay(cil) - startOfDay(zaklad)) / 86400000);
}

/**
 * Připraví čekající pozvánky pro zvoneček.
 *
 * Pravidla (každé kvůli konkrétní pasti):
 *  • nepole / null položky se přeskočí — jedna null v poli shodila celý panel
 *    (týž vzor jako NotificationCenter, viz docs),
 *  • pozvánka bez `id` se přeskočí — nešlo by ji přijmout ani odmítnout,
 *  • UŽ VYPRŠELÁ (`dny < 0`) se přeskočí — backend ji stejně nepřijme a
 *    poradce by klikal do prázdna,
 *  • `dny === null` (nečitelné datum) položku NEVYHAZUJE. Pozvánka platí i bez
 *    čitelné lhůty; jen se u ní lhůta neukáže. Zahodit ji kvůli formátu data
 *    by byla horší chyba než ji ukázat bez odpočtu.
 *
 * Vrací pole { id, kdo, email, expiruje, dny, naléhavá } od nejbližšího konce.
 */
export function cekajiciPozvanky(invitations, ted = new Date()) {
  if (!Array.isArray(invitations)) return [];
  const out = [];
  for (const p of invitations) {
    if (!p || typeof p !== "object") continue;
    if (!p.id) continue;
    const dny = dnuDoVyprseni(p.expires_at, ted);
    if (dny !== null && dny < 0) continue;
    out.push({
      id: p.id,
      /* Jméno zvoucího smí chybět (backend ho vrací jen u otevřené pozvánky).
       * Nikdy si ho nedomýšlíme — volající zobrazí zástupný text. */
      kdo: typeof p.inviter_name === "string" && p.inviter_name.trim() ? p.inviter_name.trim() : "",
      email: typeof p.inviter_email === "string" ? p.inviter_email : "",
      expiruje: p.expires_at || null,
      dny,
      nalehava: dny !== null && dny <= NALEHAVE_DNI,
    });
  }
  /* Bez čitelné lhůty (`dny === null`) až nakonec — řadit je mezi datované by
   * znamenalo tvářit se, že o jejich termínu něco víme. */
  out.sort((a, b) => {
    if (a.dny === null) return 1;
    if (b.dny === null) return -1;
    return a.dny - b.dny;
  });
  return out;
}

/**
 * Má zvoneček kvůli pozvánce svítit?
 * Na rozdíl od ostatních skupin NEZÁVISÍ na tom, kdy si poradce panel otevřel —
 * pozvánka je úkol, ne zpráva (viz hlavička souboru).
 */
export function pozvankaSviti(invitations, ted = new Date()) {
  return cekajiciPozvanky(invitations, ted).length > 0;
}

export default cekajiciPozvanky;
